import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getUserSubscriptionSchedulesByRestaurantIdAndDate,
  updateStatusForUserSubscriptionSchedule,
  formatDateForAPI
} from '../service/subscriptionSchedule';
import { getRestaurantId } from '../utils/JwtUtil';

// Import components
import {
  PageHeader,
  StatsCards,
  FilterSection,
  LoadingAndErrorStates,
  SchedulesList,
  ScheduleDetailModal,
  getStatusConfig,
  formatTimeRange,
  getScheduleStats,
  filterSchedules
} from '../components/CustomerMeals';

export default function CustomerMeals() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [timeSlotFilter, setTimeSlotFilter] = useState('ALL');
  const [areaFilter, setAreaFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const restaurantId = getRestaurantId();

  const dateString = formatDateForAPI(selectedDate);

  // Query for subscription schedules
  const {
    data: schedules = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['customerMeals', restaurantId, dateString],
    queryFn: () => getUserSubscriptionSchedulesByRestaurantIdAndDate(
      restaurantId, 
      dateString
    ),
    enabled: !!restaurantId,
    refetchOnWindowFocus: true,
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  // Mutation for updating status
  const updateStatusMutation = useMutation({
    mutationFn: ({ scheduleId, status }) => 
      updateStatusForUserSubscriptionSchedule(scheduleId, status),
    onSuccess: () => {
      queryClient.invalidateQueries(['customerMeals']);
      setSelectedSchedule(null);
    },
    onError: (error) => {
      console.error('Failed to update status:', error);
    }
  });

  // Get configuration and stats
  const statusConfig = getStatusConfig(t);
  const stats = getScheduleStats(schedules);
  const filteredSchedules = filterSchedules(schedules, statusFilter, searchTerm, timeSlotFilter, areaFilter);

  const handleMarkReady = (schedule) => {
    updateStatusMutation.mutate({
      scheduleId: schedule.id,
      status: 'READY_TO_DELIVER'
    });
  };

  if (!restaurantId) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{t('noRestaurant', 'No restaurant selected.')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <PageHeader refetch={refetch} isLoading={isLoading} />

      {/* Stats Cards */}
      <StatsCards stats={stats} />

      {/* Filters */}
      <FilterSection
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        timeSlotFilter={timeSlotFilter}
        setTimeSlotFilter={setTimeSlotFilter}
        areaFilter={areaFilter}
        setAreaFilter={setAreaFilter}
        schedules={schedules}
      />

      {/* Loading and Error States */}
      <LoadingAndErrorStates isLoading={isLoading} error={error} />

      {/* Schedules List */}
      {!isLoading && !error && (
        <SchedulesList
          filteredSchedules={filteredSchedules}
          statusConfig={statusConfig}
          handleMarkReady={handleMarkReady}
          setSelectedSchedule={setSelectedSchedule}
          updateStatusMutation={updateStatusMutation}
          formatTimeRange={formatTimeRange}
        />
      )}

      {/* Detail Modal */}
      <ScheduleDetailModal
        selectedSchedule={selectedSchedule}
        setSelectedSchedule={setSelectedSchedule}
        statusConfig={statusConfig}
        formatTimeRange={formatTimeRange}
      />
    </div>
  );
}
